import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import {
  IntlContext,
  Language,
  UPSAPIContext,
  UPSContext,
} from "@app/provider";
import DateOfBirthVerification from "@app/preBill/components/dateOfBirthVerification";
import acknowledgements from "@etc/acknowledgements.json";
import { routes } from "@app/routing";
import { useQuery } from "@app/utils";
import { logMsg } from "@app/utils/logMsg";
import UPSDobVerificationFailed from "@app/ups/pages/private/upsDobVerificationFailed";
import { DOBData } from "@app/postBill/models";
import { GuestValidationInputData, UPSErrors } from "@app/ups/models";
import { CommonErrorNotification, Notification } from "@app/components";
import { useUPSHeapEvent } from "@app/ups/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { useTokenizedLinks } from "@app/hooks";

import "./upsGuestLogin.scss";
import "@app/preBill/preBill.scss";
import { updateUpsLocalStorage } from "@app/ups/utils/localStorage";

const messages = defineMessages({
  upsGuestLoginPageTitle: {
    id: "upsGuestLoginPageTitle",
    defaultMessage: "Verify your information",
  },
  upsGuestLoginPageSubtitle: {
    id: "upsGuestLoginPageSubtitle",
    defaultMessage: "Please enter your date of birth.",
  },
  upsGuestLoginDateOfBirthVerificationIssues: {
    id: "upsGuestLoginDateOfBirthVerificationIssues",
    defaultMessage: "Need help? {contactUs}",
  },
  upsGuestLoginContactUs: {
    id: "upsGuestLoginContactUs",
    defaultMessage: "Contact Us",
  },
  upsGuestLoginCookies: {
    id: "upsGuestLoginCookies",
    defaultMessage:
      "This site uses cookies to enhance your experience. Any cookies preferences selected on Natera.com will not apply.",
  },
  upsGuestLoginAcknowledgements: {
    id: "upsGuestLoginAcknowledgements",
    defaultMessage:
      "By continuing you accept our {termsOfUse} and {privacyPolicy}",
  },
  upsGuestLoginTermsOfUse: {
    id: "upsGuestLoginTermsOfUse",
    defaultMessage: "Terms of Use",
  },
  upsGuestLoginPrivacyPolicy: {
    id: "upsGuestLoginPrivacyPolicy",
    defaultMessage: "Privacy Policy",
  },
  upsGuestLoginValidationError: {
    id: "upsGuestLoginValidationError",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
});

const UPSGuestLogin: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { redirectionMap } = useContext(UPSContext);
  const { guestValidation } = useContext(UPSAPIContext);
  const { changeLanguage } = useContext(IntlContext);

  type ErrorType = "validation" | "generic";

  const [loading, setLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<ErrorType>("validation");
  const ATTEMPTS = 3;
  const [attemptsLeft, setAttemptsLeft] = useState<number>(ATTEMPTS);
  const { trackUPSHeapEvent } = useUPSHeapEvent();
  const { getPatientUidFromTokenHandler } = useTokenizedLinks();
  const { upsPatientUid, setUPSPatientUid } = useContext(UPSContext);

  const query = useQuery();
  const guest_token = query.get("guest_token");

  if (!guest_token) {
    navigate(routes.home, { replace: true });
  }

  const handleRedirection = (linkType: string) => {
    navigate(
      redirectionMap[linkType] ||
        `${routes.upsGuestLogin}?guest_token=${guest_token}`
    );
  };

  useEffect(() => {
    changeLanguage(Language.EN);
  }, []);

  const fetchAndSetPatientUid = async (token: string) => {
    const payload = await getPatientUidFromTokenHandler(token);
    if (payload?.patientUid) {
      setUPSPatientUid(payload.patientUid);
    } else {
      logMsg("warn", "ups.login", "Can't obtain patient id from token!");
    }
  };

  useEffect(() => {
    if (upsPatientUid) {
      trackUPSHeapEvent({ eventKey: HEAP_EVENTS.ups_view_tokenizedlogin });
    }
  }, [upsPatientUid]);

  useEffect(() => {
    updateUpsLocalStorage("authType", "guest");
    updateUpsLocalStorage("guestToken", guest_token || null);
    fetchAndSetPatientUid(guest_token!);
  }, [guest_token]);

  const title: ReactElement = (
    <section className="ups-guest-login-page__container__title">
      <h2>{intl.formatMessage(messages.upsGuestLoginPageTitle)}</h2>
      <span>{intl.formatMessage(messages.upsGuestLoginPageSubtitle)}</span>
    </section>
  );

  const handleGuestValidation = async (data: GuestValidationInputData) => {
    setShowError(false);
    setLoading(true);

    try {
      const response = await guestValidation(data);
      updateUpsLocalStorage("authType", "guest");
      updateUpsLocalStorage("guestToken", guest_token);
      handleRedirection(response?.data?.link_type as string);
    } catch (error) {
      const errorCode = error.message as UPSErrors;

      if (errorCode === UPSErrors.TOKEN_EXPIRED)
        navigate(routes.upsNoLongerAvailable);
      else if (errorCode === UPSErrors.INCORRECT_DOB) {
        setAttemptsLeft((prev) => {
          const next = prev - 1;
          if (next > 0) {
            setErrorType("validation");
            setShowError(true);
          }
          return next;
        });
      } else {
        setErrorType("generic");
        setShowError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDateOfBirthSubmit = (date: DOBData) => {
    const day = date.day.toString().padStart(2, "0");
    const month = date.month.toString().padStart(2, "0");

    const guestData: GuestValidationInputData = {
      guest_token: guest_token as string,
      dob: `${date.year}-${month}-${day}`,
    };

    handleGuestValidation(guestData);
  };

  const renderNotification = () => {
    if (!showError) return null;

    if (errorType === "generic") {
      return <CommonErrorNotification />;
    }

    return (
      <Notification
        className="ups-guest-login-page__container__notification"
        type="error"
      >
        <p className="ups-guest-login-page__container__notification__content">
          {intl.formatMessage(messages.upsGuestLoginValidationError)}
        </p>
      </Notification>
    );
  };

  if (attemptsLeft <= 0) {
    return (
      <UPSDobVerificationFailed
        onCreateAccount={() => navigate(routes.signUp)}
      />
    );
  }

  return (
    <div className="ups-guest-login-page billing-gradient">
      <div className="ups-guest-login-page__container">
        {title}
        {renderNotification()}
        <DateOfBirthVerification
          loading={loading}
          onSubmit={handleDateOfBirthSubmit}
        />
        <p className="ups-guest-login-page__container__bottom-text">
          {intl.formatMessage(
            messages.upsGuestLoginDateOfBirthVerificationIssues,
            {
              contactUs: (
                <a
                  target="_blank"
                  key="privacyPolicy"
                  rel="noreferrer"
                  href={acknowledgements.links.contactUs}
                >
                  {intl.formatMessage(messages.upsGuestLoginContactUs)}
                </a>
              ),
            }
          )}
        </p>
        <div className="ups-guest-login-page__container__footer">
          <p className="ups-guest-login-page__container__footer-cookies">
            {intl.formatMessage(messages.upsGuestLoginCookies)}
          </p>
          <p className="ups-guest-login-page__container__footer-terms">
            {intl.formatMessage(messages.upsGuestLoginAcknowledgements, {
              termsOfUse: (
                <a
                  href={acknowledgements.links.termsOfUse}
                  key="termsOfUse"
                  target="_blank"
                  rel="noreferrer"
                >
                  {intl.formatMessage(messages.upsGuestLoginTermsOfUse)}
                </a>
              ),
              privacyPolicy: (
                <a
                  href={acknowledgements.links.privacyPolicy}
                  key="privacyPolicy"
                  target="_blank"
                  rel="noreferrer"
                >
                  {intl.formatMessage(messages.upsGuestLoginPrivacyPolicy)}
                </a>
              ),
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UPSGuestLogin;
